import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Tippy from '@tippy.js/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { routes } from 'routes';
import { Link } from 'react-router-dom';
import { ScrollableListViewComponent } from 'sharedComponents';
import {
    IconWarningType,
    IconLimits,
    IconTrend,
    IconInfo
} from 'svgIcons/MotionPortalIcons';
import { getEnvironmentStageSelector } from 'helpers/selectors';
import { onlineHelpLinks, apiCallStatus } from 'helpers/constants';
import colors from 'theme/_colors.scss';
import './style.scss';
import { LEFT_PANEL_WIDTH_PERCENT } from '../constants';
import useUserCapability from 'sharedHooks/useUserCapability';

const ScrollableListComponent = ({
    noDataLabel,
    className,
    loadingStatus,
    hasMore,
    showThreshold,
    showTrend,
    pageSize,
    loadMore,
    renderList,
    selectedAsset,
    onHorizontalScroll,
    leftPanelSize,
    layoutClassName,
    hasData,
    rowHeight,
    listType,
    noWrapper,
}) => {
    const { t: translate } = useTranslation();
    const [horizontalScrollActive, setHorizontalScrollActive] = useState(false);
    const horizontalScrollRef = useRef(null);
    const selectedTab = useSelector(state => state?.conditionIndicesV2?.selectedTab);
    const headerRef = useRef(null);
    const [isHeaderLabelVisible, setIsHeaderLabelVisible] = useState(true);
    const userCapabilities = useUserCapability();
    const listContentRef = useRef(null);
    const [listContentHeight, setListContentHeight] = useState(0);

    // Measures the flex-sized content area so the virtualized list exactly fills available space.
    useEffect(() => {
        const node = listContentRef.current;
        if (!node) { return; }
        const updateHeight = () => setListContentHeight(node.clientHeight);
        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const { i18n } = useTranslation();
    const environment = useSelector(getEnvironmentStageSelector);
    const languageCode = i18n.language;

    /* START - FOR CUSTOM SCROLL FUNCTIONALITY TO SCROLL TILL LAST POSITION AFTER LOAD NEXT PAGE */
    const savedScrollTop = null;

    const prevDataLenRef = useRef(hasData?.length ?? 0);
    const restoreRafRef = useRef(null);

    const saveScrollPositionToRedux = () => {
        return;
    };

    const restoreScrollPositionFromRedux = () => {
        return;
    };

    useEffect(() => {
        if (savedScrollTop === null) { return; }
        restoreScrollPositionFromRedux();
    }, [savedScrollTop]);

    useEffect(() => {
        const currentLen = hasData?.length ?? 0;
        const prevLen = prevDataLenRef.current;

        if (savedScrollTop !== null && currentLen > prevLen) {
            restoreScrollPositionFromRedux();
        }

        prevDataLenRef.current = currentLen;
    }, [hasData, savedScrollTop]);

    useEffect(() => {
        return () => {
            if (restoreRafRef.current) {
                cancelAnimationFrame(restoreRafRef.current);
            }
        };
    }, []);
    /* END - FOR CUSTOM SCROLL FUNCTIONALITY TO SCROLL TILL LAST POSITION AFTER LOAD NEXT PAGE */

    useEffect(() => {
        if (leftPanelSize > 50) {
            setIsHeaderLabelVisible(true);
        } else {
            setIsHeaderLabelVisible(false);
        }
    }, [leftPanelSize]);

    const onHorizontalScrollList = () => {
        const {
            scrollLeft,
            scrollTop,
            scrollHeight,
            clientHeight
        } = horizontalScrollRef.current;
        if (horizontalScrollRef?.current) {
            if (scrollLeft > 0) {
                setHorizontalScrollActive(true);
            } else {
                setHorizontalScrollActive(false);
            }
            onHorizontalScroll(scrollLeft > 0);
        }
        // For FixedSizeList the virtual list fires its own onScroll → onLoadMore.
        // The outer container only handles horizontal overflow and its scrollHeight
        // always equals clientHeight (scrollTop is always 0), which would make the
        // condition below permanently true and fire loadMore on every scroll event.
        if (listType !== 'FixedSizeList' && hasMore && loadMore && typeof loadMore === 'function' &&
            loadingStatus !== apiCallStatus.LOADING &&
            horizontalScrollRef.current) {
            if (Math.ceil(scrollTop) + 1 + clientHeight >= scrollHeight) {
                loadMore();
            }
        }
    };

    const InfoTooltip = ({ title, isLink, children, placement = 'bottom' }) => {
        return (
            <Tippy
                content={
                    <>
                        <div className='info-tooltip__title'>
                            {title}
                        </div>
                        <div className='info-tooltip__body'>
                            {children}
                        </div>
                        {isLink &&
                            <a className='info-tooltip__link' rel='noreferrer' target='_blank' href={`${onlineHelpLinks[environment]}${languageCode}`} role='link' >
                                {translate('ABB.Powertrain.Frontend.assetOverviewTooltipLinkText')}
                            </a>
                        }
                    </>
                }
                className='asset-overview-tooltip'
                interactive={true}
                interactiveBorder={10}
                animation='scale'
                theme='light-border'
                trigger='click'
                placement={placement}
                maxWidth='400px'
                boundary='viewport'
                appendTo={document.body}
            >
                <button type='button' className='info-icon-button' aria-label={title || translate('ABB.Powertrain.Frontend.assetOverviewTooltipLinkText')} onClick={e => e.stopPropagation()}>
                    <IconInfo width={'16'} height={'16'} color={'black'} />
                </button>
            </Tippy>
        );
    };

    const renderHeader = () => {
        const isTitleVisible = leftPanelSize >= LEFT_PANEL_WIDTH_PERCENT;
        const isLeftpanelSizeSmall = leftPanelSize <= 50;
        return <div className='asset-list-header'>
            <div
                className={`
                header-item header-item--cell-fixed
                ${horizontalScrollActive ? 'horizontal-scroll-active' : ''}
                ${className} ${loadingStatus === apiCallStatus.LOADING ? 'loading' : ''}`}
                style={selectedAsset?.assetName ? { minWidth: '100px' } : {}}
            >
                {translate('ABB.Powertrain.Frontend.conditionIndicesOverviewNameColumnTitle')}
            </div>
            {isTitleVisible && showThreshold &&
                <div className={`header-item ${isLeftpanelSizeSmall ? 'left-sidebar-header-cell-open' : 'left-sidebar-header-cell-closed'}`} ref={headerRef}>
                    <div className='column-icon'>
                        <IconWarningType color={colors.black} />
                    </div>
                    {isHeaderLabelVisible && translate('ABB.Powertrain.Frontend.conditionIndicesOverviewThresholdColumnTitle')}
                    <InfoTooltip
                        title={`${translate('ABB.Powertrain.Frontend.assetOverviewConditionIndicesText')} - ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewThresholdColumnTitle')}`}
                        isLink={true}
                    >
                        <div className='threshold-content'>
                            <p className='threshold-content__para'>{translate('ABB.Powertrain.Frontend.assetOverviewThresholdFirstParagraph')}</p>
                        </div>
                    </InfoTooltip>
                </div>}
            {isTitleVisible && showTrend &&
                <div className={`header-item ${isLeftpanelSizeSmall ? 'left-sidebar-header-cell-open' : 'left-sidebar-header-cell-closed'}`}>
                    <div className='column-icon'>
                        <IconTrend />
                    </div>
                    {userCapabilities?.hasMotorAnomalyDetection() ?
                        <>
                            {isHeaderLabelVisible &&
                                `${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')} & ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAnomalyColumnTitle')}`
                            }
                            <InfoTooltip
                                title={`${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')} & ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAnomalyColumnTitle')}`}
                                isLink={true}
                            >
                                <div className='trend-content'>
                                    <p>{translate('ABB.Powertrain.Frontend.conditionIndicesOverviewColumnInfoText')}</p>
                                    <div>
                                        <p><span className='title'>{translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')}</span> {translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnInfoText')}</p>
                                        <p><span className='title'>{translate('ABB.Powertrain.Frontend.anomalyDetectionTab')}</span> {translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAnomalyColumnInfoText')}</p>
                                    </div>
                                    <p>{translate('ABB.Powertrain.Frontend.conditionIndicesOverviewSeverityPerspectiveText')}</p>
                                </div>
                            </InfoTooltip>
                        </> : <>
                            {isHeaderLabelVisible && translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')}
                            <InfoTooltip
                                title={`${translate('ABB.Powertrain.Frontend.assetOverviewConditionIndicesText')} - ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewTrendColumnTitle')}`}
                                isLink={true}
                            >
                                <div className='trend-content'>
                                    <p className='trend-content__para'>{translate('ABB.Powertrain.Frontend.assetOverviewTrendFirstParagraph')}</p>
                                </div>
                            </InfoTooltip>
                        </>
                    }
                </div>
            }
            {
                isTitleVisible && <div className={`header-item ${isLeftpanelSizeSmall ? 'left-sidebar-header-cell-open' : 'left-sidebar-header-cell-closed'}`}>
                    <IconLimits />
                    {isHeaderLabelVisible && translate('ABB.Powertrain.Frontend.limitsTitle')}
                    <InfoTooltip
                        title={translate('ABB.Powertrain.Frontend.assetOverviewLimitsText')}
                        isLink={false}
                    >
                        <div className='limit-content'>
                            <p className='limit-content__para'>{translate('ABB.Powertrain.Frontend.assetOverviewLimitFirstParagraph')}</p>
                            <p className='limit-content__para'>
                                <b>{translate('ABB.Powertrain.Frontend.assetOverviewLimitPointTitle')}</b>
                                <span>{translate('ABB.Powertrain.Frontend.assetOverviewLimitPointContent')}</span>
                            </p>
                        </div>
                    </InfoTooltip>
                </div>
            }
        </div >;
    };

    // Calculate the index of selected asset in the data array
    const selectedAssetIndex = selectedAsset && hasData
        ? hasData.findIndex(item => item?.id === selectedAsset?.id || item?.assetId === selectedAsset?.assetId)
        : -1;

    const handleVirtualOnLoadMore = () => {
        saveScrollPositionToRedux();
        loadMore?.();
    };

    const renderBottomSpace = () =>
        <div className='list-view__bottom-space'>
            {translate('ABB.Powertrain.Frontend.conditionOverviewConfigurePowertrainsLabel')} <Link className='list-view__bottom-space__link' to={{ pathname: routes.ToolsPowertrainConfigurationV2 }}>{translate('ABB.Powertrain.Frontend.conditionOverviewConfigurePowertrainsLinkLabel')}</Link>
        </div>
        ;

    const hasRealData = hasData && hasData.length > 0;
    // Sentinel row so the footer link scrolls with the list instead of a fixed bar below it.
    const listDataWithFooter = useMemo(
        () => hasRealData ? [...hasData, { __isListFooter: true }] : hasData,
        [hasData, hasRealData]
    );

    const renderRowWithFooter = useCallback(
        ({ item, index, toggleSize }) => item?.__isListFooter ? renderBottomSpace() : renderList({ item, index, toggleSize }),
        [renderList]
    );

    return (
        <div className={`overview-list-v2 resizable-layout-v2 ${layoutClassName} ${selectedAsset ? 'panel-open' : ''}`}>
            <div className='left-container w-100'>
                <div
                    role='scrollable-list'
                    className={`overview-scrollable-list-v2 ${className} ${loadingStatus === apiCallStatus.LOADING ? 'overview-scrollable-list__render' : ''}`}
                    onScroll={onHorizontalScrollList}
                    ref={horizontalScrollRef}
                >
                    <div className={`list-item ${selectedTab?.toLowerCase()}`}>
                        {renderHeader()}
                        <div className='list-item__content' ref={listContentRef}>
                            <ScrollableListViewComponent
                                emptyLabel={noDataLabel}
                                withLoading={true}
                                loadingStatus={loadingStatus}
                                hasMore={hasMore}
                                pageSize={pageSize}
                                onLoadMore={handleVirtualOnLoadMore}
                                noWrapper={noWrapper}
                                data={listDataWithFooter || []}
                                rowHeight={rowHeight}
                                height={listContentHeight}
                                renderRow={renderRowWithFooter}
                                listType={listType}
                                initialScrollIndex={selectedAssetIndex >= 0 ? selectedAssetIndex : undefined}
                            />
                        </div>
                    </div>
                </div>
                {!hasRealData && renderBottomSpace()}
            </div>
        </div>
    );
};

ScrollableListComponent.propTypes = {
    noDataLabel: PropTypes.string,
    className: PropTypes.string,
    loadingStatus: PropTypes.string,
    hasMore: PropTypes.bool,
    pageSize: PropTypes.number,
    loadMore: PropTypes.func,
    renderList: PropTypes.func,
    selectedAsset: PropTypes.object,
    showThreshold: PropTypes.bool,
    showTrend: PropTypes.bool,
    hasData: PropTypes.array,
    selectedPowertrain: PropTypes.number,
    selectedAssetGroup: PropTypes.number,
    asset: PropTypes.object,
    showAsset: PropTypes.bool,
    onHorizontalScroll: PropTypes.func,
    onLabelVisibilityChange: PropTypes.func,
    title: PropTypes.string,
    isLink: PropTypes.bool,
    children: PropTypes.node,
    placement: PropTypes.string
};

export default ScrollableListComponent;
